import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Container } from "reactstrap";
import styled from "styled-components";
import AddressAutocomplete from "../../components/AddressAutocomplete/AddressAutocomplete";
import NewFooter from "../../components/Footers/NewFooter";
import { ImportProgressPanel } from "../../components/ListingImportProgress/ListingImportProgress";
import NewNavbar from "../../components/Navbar/NewNavbar";
import { PageContainer } from "../../components/styled/shared";
import useZillowImport from "../../hooks/useZillowImport";
import { flattenError } from "../../utils/errorDisplay";
import { storageGet, storageRemove, storageSet } from "../../utils/storage";
import { ZILLOW_URL_ERROR, normalizeZillowUrl } from "../../utils/zillowUrl";

const MainContent = styled.div`
  flex: 1;
  padding: 140px 0 120px;

  @media (max-width: 767.98px) {
    padding: 110px 0 80px;
  }
`;

const PageTitle = styled.h1`
  font-size: 48px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--brand-ink);
  text-align: center;
  margin-bottom: 16px;

  @media (max-width: 767.98px) {
    font-size: 34px;
  }
`;

const PageSubtitle = styled.p`
  font-size: 18px;
  color: var(--brand-ink-3);
  text-align: center;
  max-width: 620px;
  margin: 0 auto 36px;
  line-height: 1.6;
`;

const FormSection = styled.div`
  max-width: 560px;
  margin: 0 auto;
  padding: 28px;
  background: #fff;
  border: 1px solid var(--brand-border);
  border-radius: var(--brand-radius-xl, 20px);
  box-shadow: var(--brand-shadow-md, 0 12px 32px rgba(15, 23, 42, 0.08));

  @media (max-width: 560px) {
    padding: 20px;
  }
`;

const LoadingNote = styled.output`
  display: block;
  text-align: center;
  color: var(--brand-primary-700);
  font-size: 14px;
  font-weight: 500;
  margin: 16px 0 0;
`;

const OrDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  color: var(--brand-ink-4);
  font-size: 13px;
  margin: 16px 0 12px;

  &::before,
  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: var(--brand-border);
  }
`;

const SecondaryRow = styled.form`
  display: flex;
  flex-direction: row;
  gap: 10px;
  align-items: center;

  @media (max-width: 560px) {
    flex-direction: column;
  }
`;

const StyledInput = styled.input`
  width: 100%;
  height: 48px;
  padding: 0 18px;
  border: 1px solid var(--brand-border);
  border-radius: var(--brand-radius-lg);
  font-size: 15px;
  color: var(--brand-ink);
  background: #fff;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus {
    outline: none;
    border-color: var(--brand-primary);
    box-shadow: 0 0 0 3px rgba(14, 126, 122, 0.12);
  }

  &:disabled {
    background: var(--brand-bg-soft);
    cursor: not-allowed;
  }

  &::placeholder {
    color: var(--brand-ink-4);
  }
`;

const ContinueButton = styled.button`
  flex: 0 0 auto;
  height: 52px;
  padding: 0 22px;
  background: var(--brand-primary);
  border: 1px solid var(--brand-primary);
  color: #fff;
  border-radius: var(--brand-radius-lg);
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover:not(:disabled) {
    background: var(--brand-primary-50);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: 560px) {
    width: 100%;
  }
`;

const ErrorMessage = styled.div`
  background: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 16px;
  color: #991b1b;
  font-size: 14px;
  text-align: center;
`;

function SimplifiedSubmitPage() {
  const [zillowUrl, setZillowUrl] = useState("");
  const [error, setError] = useState("");

  const history = useHistory();
  const globalLoading = useSelector((/** @type {any} */ state) => state.listings.globalLoading);
  // Backend / network failures land here via the SUBMIT_LISTING_FAIL reducer
  // case. Without surfacing it the page would silently re-enable the button
  // and look like nothing happened.
  const submitError = useSelector((/** @type {any} */ state) => state.listings.error);

  const importer = useZillowImport({
    resumePending: true,
    onReady: () => history.push("/listing-confirmation/"),
  });

  // A property picked on the homepage hero arrives with its Zillow URL
  // already resolved — pre-fill it so the user just confirms.
  useEffect(() => {
    const pending = storageGet("pendingZillowUrl");
    if (pending) {
      setZillowUrl(pending);
      storageRemove("pendingZillowUrl");
    }
  }, []);

  /** @param {string} url */
  const startFromUrl = (url) => {
    if (globalLoading || importer.busy) return;
    const normalized = normalizeZillowUrl(url);
    if (!normalized) {
      setError(ZILLOW_URL_ERROR);
      return;
    }
    setError("");
    setZillowUrl(normalized);
    void importer.start(normalized);
  };

  // Selecting an address resolves straight to a Zillow URL — no extra
  // click needed. Details get verified on the next step.
  const handleAddressSelect = (/** @type {{ zpid: number }} */ suggestion) => {
    const url = `https://www.zillow.com/homedetails/${suggestion.zpid}_zpid/`;
    setZillowUrl(url);
    startFromUrl(url);
  };

  // Zillow occasionally has no typeahead result for new construction or a
  // recently changed address. Preserve the typed address and continue through
  // the existing manual-details route instead of leaving the user at a dead
  // end. If storage is blocked, the route still opens as a blank manual form.
  /** @param {string} address */
  const handleFreeTextAddress = (address) => {
    storageSet(
      "pendingAddressPrefill",
      JSON.stringify({
        property: null,
        suggestion: { display: address, city: "", state: "", zipcode: "" },
      })
    );
    history.push("/submit");
  };

  /** @param {React.FormEvent} e */
  const handleUrlSubmit = (e) => {
    e.preventDefault();
    setError("");

    if (!zillowUrl.trim()) {
      setError("Please enter an address or listing URL");
      return;
    }
    startFromUrl(zillowUrl);
  };

  return (
    <PageContainer>
      <NewNavbar />

      <MainContent>
        <Container>
          <PageTitle>Let's start with the address.</PageTitle>
          <PageSubtitle>
            Whether you're already listed or just meeting the seller, enter the address and Listino
            pulls the property details automatically for your review.
          </PageSubtitle>

          <FormSection>
            {importer.visible ? (
              <ImportProgressPanel importer={importer} />
            ) : (
              <>
                {(error || importer.error) && (
                  <ErrorMessage role="alert">{error || importer.error}</ErrorMessage>
                )}
                {!error && submitError && (
                  <ErrorMessage>
                    {flattenError(submitError).map(({ field, message }) => (
                      <div key={`${field}-${message}`}>
                        {field ? `${field}: ${message}` : message}
                      </div>
                    ))}
                  </ErrorMessage>
                )}

                <AddressAutocomplete
                  id="addressSearch"
                  placeholder="Enter any property address…"
                  onSelect={handleAddressSelect}
                  onFreeText={handleFreeTextAddress}
                  onListingUrl={startFromUrl}
                  disabled={globalLoading}
                />

                {globalLoading && (
                  <LoadingNote aria-live="polite">Pulling the property details…</LoadingNote>
                )}

                <OrDivider>
                  <span>or paste a Zillow link</span>
                </OrDivider>

                <SecondaryRow onSubmit={handleUrlSubmit}>
                  <StyledInput
                    id="zillowUrl"
                    type="text"
                    inputMode="url"
                    aria-label="Zillow listing link"
                    autoComplete="url"
                    placeholder="https://www.zillow.com/homedetails/..."
                    value={zillowUrl}
                    onChange={(e) => setZillowUrl(e.target.value)}
                    disabled={globalLoading}
                  />
                  <ContinueButton type="submit" disabled={globalLoading}>
                    {globalLoading ? "Working…" : "Continue"}
                  </ContinueButton>
                </SecondaryRow>
              </>
            )}
          </FormSection>
        </Container>
      </MainContent>

      <NewFooter />
    </PageContainer>
  );
}

export default SimplifiedSubmitPage;
